import { useState, useRef, useEffect } from 'react';
import { profileImageUrl, useSaveProfile } from '@/hooks/useProfile';
import { validateImage } from '@/lib/auth-validation';
import { LANGUAGES } from '@/constants/languages';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { InfoItem } from '@/components/features/profile/InfoItem';
import type { LanguagesMap } from '@/types';
import type { ClientProfile } from '@/types/session';
import { ReviewsList } from '@/components/features/ratings/ReviewsList';
import { StarRating } from '@/components/features/ratings/StarRating';
import { useProfileReviews } from '@/hooks/useProfileReviews';
import { useDaoConfig } from '@/hooks/useDaoConfig';

export default function ClientProfilePage({
  profile: client,
  principalId,
  startInEditMode,
}: {
  profile: ClientProfile;
  principalId: string;
  startInEditMode?: boolean;
}) {
  const { clientRole } = useDaoConfig();
  const reviews = useProfileReviews('client');
  const ratings = reviews.data ?? [];
  const average = ratings.length
    ? ratings.reduce((sum, r) => sum + r.rating, 0) / ratings.length
    : 0;
  const [isEditing, setIsEditing] = useState(startInEditMode ?? false);
  const [formData, setFormData] = useState<ClientProfile>(client);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mutation = useSaveProfile('client');
  const avatarUrl = profileImageUrl(principalId, 'client', revision);
  const languageNames = client.languages.map((code) => LANGUAGES[code] ?? code);
  useEffect(() => {
    if (!selectedFile) {
      setImagePreview(null);
      return;
    }
    const url = URL.createObjectURL(selectedFile);
    setImagePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedFile]);
  const handleEnterEdit = () => {
    mutation.reset();
    setFormData(client);
    setError(null);
    setSelectedFile(null);
    setIsEditing(true);
  };
  const handleCancelEdit = () => {
    mutation.reset();
    setIsEditing(false);
    setSelectedFile(null);
    setError(null);
  };
  const handleFieldChange = (field: keyof ClientProfile, value: string) =>
    setFormData((old) => ({
      ...old,
      [field]: field === 'name' ? value : value || null,
    }));
  const handleLanguageToggle = (code: string) =>
    setFormData((old) => ({
      ...old,
      languages: old.languages.includes(code)
        ? old.languages.filter((value) => value !== code)
        : [...old.languages, code],
    }));
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      validateImage(file);
      setSelectedFile(file);
      setError(null);
    } catch (reason) {
      setError((reason as Error).message);
      event.target.value = '';
    }
  };
  const handleSave = async () => {
    setError(null);
    try {
      await mutation.mutateAsync({ profile: formData, image: selectedFile });
      setRevision(Date.now());
      setIsEditing(false);
      setSelectedFile(null);
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : 'Profile save failed.'
      );
    }
  };
  if (isEditing)
    return (
      <ClientProfileEdit
        formData={formData}
        avatarUrl={avatarUrl}
        imagePreview={imagePreview}
        languagesMap={{
          ...LANGUAGES,
          ...Object.fromEntries(
            formData.languages.map((code) => [code, LANGUAGES[code] ?? code])
          ),
        }}
        fileInputRef={fileInputRef}
        isSaving={mutation.isPending}
        saveError={error}
        onFieldChange={handleFieldChange}
        onLanguageToggle={handleLanguageToggle}
        onFileChange={handleFileChange}
        onSave={() => void handleSave()}
        onCancel={handleCancelEdit}
      />
    );

  return (
    <div className="flex w-full flex-col gap-10">
      {/* Profile Header - full width, surface-2 bg, border-bottom */}
      <div className="headerApp">
        <div className="headerContent flex flex-wrap items-end gap-y-6">
          <div className="headerTitle flex min-w-0 flex-1 items-center gap-[--spacing-12]">
            <div className="flex min-w-0 flex-1 items-center gap-[--spacing-10] pl-[--spacing-8]">
              {/* Avatar 72px */}
              <div className="relative flex h-[72px] w-[72px] shrink-0 items-center justify-center overflow-hidden rounded-full border border-[--base-border]">
                {/* Fallback icon (always rendered, hidden by image when loaded) */}
                <i className="ri-user-smile-line text-[40px] text-[--base-fill-5]" />
                {avatarUrl && (
                  <img
                    key={avatarUrl}
                    src={avatarUrl}
                    alt={client.name}
                    className="absolute inset-0 h-full w-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                )}
              </div>

              {/* Name + company label */}
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-4">
                  <span className="truncate text-3xl font-bold text-[--text-dark-primary]">
                    {client.name}
                  </span>
                  {client.company && (
                    <span className="shrink-0 text-xs font-medium leading-[18px] text-[--text-dark-tertiary]">
                      {client.company}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Right: DAO View button + rating */}
            <div className="flex shrink-0 items-center gap-8">
              {/* DAO View button */}
              <button
                type="button"
                disabled
                title="DAO profile integration pending"
                className="flex h-9 items-center gap-1.5 rounded-xl border border-[var(--state-brand-active,#36d399)] bg-[rgba(54,211,153,0.08)] px-3 text-sm font-semibold leading-[22px] text-[var(--state-brand-active,#36d399)] transition-colors hover:bg-[rgba(54,211,153,0.15)]"
              >
                <i
                  className="ri-government-line text-base leading-none"
                  aria-hidden="true"
                />
                DAO View
              </button>

              {!reviews.isPending && !reviews.error && (
                <StarRating rating={average} size="md" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content: two columns */}
      <div className="pageContent">
        <div className="flex w-full items-start gap-[--spacing-17]">
          {/* Left column: Profile card */}
          <div className="min-w-0 flex-1 rounded-[--radi-6] border border-[--base-border] bg-[--base-surface-2] px-[--spacing-12] py-[--spacing-10] text-left shadow-elevation-xs">
            <div className="flex flex-col gap-[--spacing-10]">
              {/* Card header: role label + edit button */}
              <div className="flex items-center justify-between">
                <div className="flex flex-1 flex-col gap-1">
                  <span className="text-lg font-medium text-[--text-dark-primary]">
                    {clientRole}
                  </span>
                  <span className="text-xs font-normal text-[--text-dark-secondary]">
                    {client.department || 'No department'}
                  </span>
                </div>
                <Button onClick={handleEnterEdit} variant="tertiary" size="sm">
                  Edit Information
                </Button>
              </div>

              {/* Description */}
              <div className="flex flex-col gap-[--spacing-3]">
                <span className="text-sm font-medium text-[--text-dark-tertiary]">
                  Description
                </span>
                <p className="text-sm font-medium text-[--text-dark-primary]">
                  {client.description || 'No description available.'}
                </p>
              </div>

              {/* Info items */}
              <div className="flex flex-col gap-[--spacing-8]">
                {client.company && (
                  <InfoItem icon="ri-building-line" text={client.company} />
                )}
                <InfoItem
                  icon="ri-translate-2"
                  text={
                    languageNames.length > 0
                      ? languageNames.join(', ')
                      : 'No languages'
                  }
                />
                <InfoItem
                  icon="ri-map-pin-line"
                  text={client.location || 'No location'}
                />
                <InfoItem
                  icon="ri-earth-line"
                  text={client.website || 'No website'}
                />
                <InfoItem
                  icon="ri-mail-line"
                  text={client.contactEmail ?? 'No contact email'}
                />
              </div>
            </div>
          </div>

          {/* Right column: Reviews */}
          <div className="min-w-0 flex-1">
            <ReviewsList
              ratings={ratings}
              totalCount={ratings.length}
              isLoading={reviews.isPending && !reviews.error}
            />
            {reviews.error && (
              <div role="alert">
                <p>{reviews.error.message}</p>
                <Button onClick={() => void reviews.refetch()}>
                  Retry reviews
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface ClientProfileEditProps {
  formData: ClientProfile;
  avatarUrl: string;
  imagePreview: string | null;
  languagesMap: LanguagesMap | undefined;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  isSaving: boolean;
  saveError: string | null;
  onFieldChange: (field: keyof ClientProfile, value: string) => void;
  onLanguageToggle: (code: string) => void;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSave: () => void;
  onCancel: () => void;
}

function ClientProfileEdit({
  formData,
  avatarUrl,
  imagePreview,
  languagesMap,
  fileInputRef,
  isSaving,
  saveError,
  onFieldChange,
  onLanguageToggle,
  onFileChange,
  onSave,
  onCancel,
}: ClientProfileEditProps) {
  const { clientRole } = useDaoConfig();
  return (
    <div className="pageContent">
      <div className="formContainer">
        <div className="mb-4 text-left">
          <h1>Edit {clientRole} Profile</h1>
          <p>{clientRole} data</p>
        </div>
        <fieldset disabled={isSaving} className="space-y-6">
          <div className="inputGroup">
            <Label htmlFor="contactEmail">Email</Label>
            <Input
              id="contactEmail"
              type="email"
              value={formData.contactEmail ?? ''}
              onChange={(e) => onFieldChange('contactEmail', e.target.value)}
            />
          </div>

          {/* Name */}
          <div className="inputGroup">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              name="name"
              value={formData.name || ''}
              onChange={(e) => onFieldChange('name', e.target.value)}
              placeholder="Name"
            />
          </div>

          {/* Description */}
          <div className="inputGroup">
            <Label htmlFor="description">Description</Label>
            <textarea
              id="description"
              name="description"
              value={formData.description || ''}
              onChange={(e) => onFieldChange('description', e.target.value)}
              className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              placeholder="Description"
            />
          </div>

          {/* Company */}
          <div className="inputGroup">
            <Label htmlFor="company">Company</Label>
            <Input
              id="company"
              name="company"
              value={formData.company || ''}
              onChange={(e) => onFieldChange('company', e.target.value)}
              placeholder="Company"
            />
          </div>

          {/* Department */}
          <div className="inputGroup">
            <Label htmlFor="department">Department</Label>
            <Input
              id="department"
              name="department"
              value={formData.department || ''}
              onChange={(e) => onFieldChange('department', e.target.value)}
              placeholder="Department"
            />
          </div>

          {/* Website */}
          <div className="inputGroup">
            <Label htmlFor="website">Website</Label>
            <Input
              id="website"
              name="website"
              value={formData.website || ''}
              onChange={(e) => onFieldChange('website', e.target.value)}
              placeholder="Website"
            />
          </div>

          {/* Location */}
          <div className="inputGroup">
            <Label htmlFor="location">Location</Label>
            <Input
              id="location"
              name="location"
              value={formData.location || ''}
              onChange={(e) => onFieldChange('location', e.target.value)}
              placeholder="Location"
            />
          </div>

          {/* Languages multi-select */}
          <div className="inputGroup">
            <Label>Languages</Label>
            <div className="input flex max-h-48 flex-col space-y-1 overflow-y-auto rounded-md border border-input bg-background p-2">
              {languagesMap ? (
                Object.entries(languagesMap).map(([code, name]) => {
                  const isSelected = (formData.languages || []).includes(code);
                  return (
                    <label
                      key={code}
                      className={`flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm transition-colors ${
                        isSelected
                          ? 'bg-[--colors-emerald-50] !text-[--colors-emerald-500]'
                          : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onLanguageToggle(code)}
                        className="customCheckbox"
                      />
                      <span>
                        {code} - {name}
                      </span>
                    </label>
                  );
                })
              ) : (
                <p className="text-sm text-muted-foreground">
                  Loading languages...
                </p>
              )}
            </div>
          </div>

          {/* Photo upload */}
          <div className="inputGroup">
            <Label>Profile Photo (PNG, JPEG or WebP, max 1 MiB)</Label>
            <div className="mt-2 flex items-center gap-4">
              <img
                className="aspect-square w-[72px] overflow-hidden rounded-full border-2 border-border object-cover"
                key={imagePreview || avatarUrl}
                src={imagePreview || avatarUrl}
                alt="Profile preview"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = '/images/none.png';
                }}
              />
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Select file...
                </Button>
                <input
                  ref={fileInputRef as React.RefObject<HTMLInputElement>}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={onFileChange}
                />
              </div>
            </div>
          </div>

          {/* Error message */}
          {saveError && (
            <div className="rounded-md border border-red-500/30 bg-red-500/15 p-3">
              <p role="alert" className="text-sm text-red-400">
                {saveError}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex w-full items-center justify-between gap-4 border-t border-border pt-4 [&>button]:min-w-[128px]">
            <Button variant="outline" onClick={onCancel} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={onSave} isLoading={isSaving}>
              Save
            </Button>
          </div>
        </fieldset>
      </div>
    </div>
  );
}
